import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('orders');
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [content, setContent] = useState([]);
  const [newInvitation, setNewInvitation] = useState({ email: '', access_type: 'digital' });
  const [loading, setLoading] = useState(true);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderUpdate, setOrderUpdate] = useState({ shipping_status: '', tracking_number: '' });
  const [editingContent, setEditingContent] = useState(null);
  const [contentUpdate, setContentUpdate] = useState({ page: '', section: '', content: '' });
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'analytics') {
        const response = await axios.get(`${API}/admin/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAnalytics(response.data);
      } else if (activeTab === 'users') {
        const response = await axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsers(response.data.users);
      } else if (activeTab === 'orders') {
        const response = await axios.get(`${API}/admin/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(response.data.orders);
      } else if (activeTab === 'invitations') {
        const response = await axios.get(`${API}/admin/invitations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInvitations(response.data.invitations);
      } else if (activeTab === 'content') {
        const response = await axios.get(`${API}/admin/content`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setContent(response.data.content);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('U het nie admin toegang nie');
        navigate('/');
      } else {
        toast.error('Kon nie data laai nie');
      }
    } finally {
      setLoading(false);
    }
  };

  const grantAccess = async (userEmail) => {
    try {
      await axios.post(
        `${API}/admin/grant-access/${userEmail}`,
        { access_type: 'digital' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Gratis toegang toegestaan');
      loadData();
    } catch (error) {
      toast.error('Kon nie toegang toestaan nie');
    }
  };

  const revokeAccess = async (userEmail) => {
    if (!window.confirm(`Wil u regtig toegang verwyder vir ${userEmail}?`)) {
      return;
    }
    try {
      const response = await axios.post(
        `${API}/admin/revoke-access/${userEmail}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Revoke response:', response.data);
      toast.success('Toegang suksesvol verwyder');
      // Force reload the users data
      await loadData();
    } catch (error) {
      console.error('Revoke error:', error);
      toast.error('Kon nie toegang verwyder nie');
    }
  };

  const createInvitation = async () => {
    if (!newInvitation.email) {
      toast.error('Verskaf asseblief e-pos adres');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/invitations`,
        { email: newInvitation.email, access_type: newInvitation.access_type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Uitnodiging geskep!');
      setNewInvitation({ email: '', access_type: 'digital' });
      // Try to copy to clipboard, but don't fail if it doesn't work
      try {
        await navigator.clipboard.writeText(response.data.invitation_link);
        toast.info('Uitnodiging skakel gekopieer!');
      } catch (clipboardError) {
        console.log('Clipboard copy failed:', clipboardError);
        toast.info(`Skakel: ${response.data.invitation_link}`);
      }
      loadData();
    } catch (error) {
      toast.error('Kon nie uitnodiging skep nie');
    }
  };

  const updateOrderStatus = async (purchaseId) => {
    try {
      await axios.put(
        `${API}/admin/orders/${purchaseId}/status`,
        orderUpdate,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Bestelling status opgedateer');
      setEditingOrder(null);
      setOrderUpdate({ shipping_status: '', tracking_number: '' });
      loadData();
    } catch (error) {
      toast.error('Kon nie status opdateer nie');
    }
  };

  const updateContent = async () => {
    if (!contentUpdate.page || !contentUpdate.section || !contentUpdate.content) {
      toast.error('Vul asseblief alle velde in');
      return;
    }
    try {
      await axios.put(
        `${API}/admin/content`,
        contentUpdate,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Inhoud opgedateer');
      setEditingContent(null);
      setContentUpdate({ page: '', section: '', content: '' });
      loadData();
    } catch (error) {
      toast.error('Kon nie inhoud opdateer nie');
    }
  };

  const resetStatistics = async () => {
    if (!window.confirm('Wil u regtig alle besoeker statistieke uitvee? Hierdie aksie kan nie ongedaan gemaak word nie.')) {
      return;
    }
    try {
      const response = await axios.post(
        `${API}/admin/reset-statistics`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Statistieke teruggestel! ${response.data.deleted.visits} besoeke verwyder.`);
      loadData();
    } catch (error) {
      toast.error('Kon nie statistieke terugstel nie');
    }
  };

  const resetSales = async () => {
    if (!window.confirm('Wil u regtig alle verkope/aankope data uitvee? Alle gebruikers sal hul toegang verloor. Hierdie aksie kan nie ongedaan gemaak word nie.')) {
      return;
    }
    try {
      const response = await axios.post(
        `${API}/admin/reset-sales`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Verkope teruggestel! ${response.data.deleted.purchases} aankope verwyder.`);
      loadData();
    } catch (error) {
      toast.error('Kon nie verkope terugstel nie');
    }
  };

  const resetUsers = async () => {
    if (!window.confirm('WAARSKUWING: Wil u regtig ALLE gebruikers uitvee? Hulle sal nie meer kan aanmeld nie. Hierdie aksie kan NIE ongedaan gemaak word nie!')) {
      return;
    }
    try {
      const response = await axios.post(
        `${API}/admin/reset-users`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Gebruikers teruggestel! ${response.data.deleted.users} gebruikers verwyder.`);
      loadData();
    } catch (error) {
      toast.error('Kon nie gebruikers terugstel nie');
    }
  };

  // Export to Excel (CSV format)
  const exportToExcel = (data, filename, columns) => {
    // Create CSV content with BOM for Excel UTF-8 compatibility
    const BOM = '\uFEFF';
    const headers = columns.map(col => col.label).join(',');
    const rows = data.map(item => 
      columns.map(col => {
        let value = col.getValue(item);
        // Escape quotes and wrap in quotes if contains comma
        if (value === null || value === undefined) value = '';
        value = String(value).replace(/"/g, '""');
        if (value.includes(',') || value.includes('\n') || value.includes('"')) {
          value = `"${value}"`;
        }
        return value;
      }).join(',')
    ).join('\n');
    
    const csv = BOM + headers + '\n' + rows;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Excel lêer afgelaai!');
  };

  const exportOrders = () => {
    const columns = [
      { label: 'Bestelling ID', getValue: (o) => o.purchase_id },
      { label: 'Datum', getValue: (o) => new Date(o.created_at).toLocaleString('af-ZA') },
      { label: 'Kliënt Naam', getValue: (o) => o.user_name },
      { label: 'E-pos', getValue: (o) => o.user_email },
      { label: 'Telefoon', getValue: (o) => o.phone_number || '' },
      { label: 'Tipe', getValue: (o) => o.purchase_type === 'digital' ? 'Digitaal' : o.purchase_type === 'physical' ? 'Fisies' : 'Beide' },
      { label: 'Bedrag (R)', getValue: (o) => o.amount?.toFixed(2) || '0.00' },
      { label: 'Betaalstatus', getValue: (o) => o.payment_status === 'completed' ? 'Betaal' : 'Hangend' },
      { label: 'Versendstatus', getValue: (o) => getStatusLabel(o.shipping_status || 'pending') },
      { label: 'Spoorsnommer', getValue: (o) => o.tracking_number || '' },
      { label: 'Adres', getValue: (o) => o.shipping_address?.replace(/\n/g, ' ') || '' }
    ];
    exportToExcel(orders, 'bestellings', columns);
  };

  const exportUsers = () => {
    const columns = [
      { label: 'Gebruiker ID', getValue: (u) => u.user_id },
      { label: 'Naam', getValue: (u) => u.name },
      { label: 'E-pos', getValue: (u) => u.email },
      { label: 'Registrasiedatum', getValue: (u) => new Date(u.created_at).toLocaleString('af-ZA') },
      { label: 'Het Toegang', getValue: (u) => (u.has_purchased || u.has_digital_access) ? 'Ja' : 'Nee' },
      { label: 'Toegang Tipe', getValue: (u) => u.purchase_type || 'Geen' }
    ];
    exportToExcel(users, 'gebruikers', columns);
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Hangend',
      processing: 'Verwerk',
      shipped: 'Gestuur',
      delivered: 'Afgelewer'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-600';
  };

  // Format chart data
  const formatChartDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getDate()}/${date.getMonth() + 1}`;
  };

  if (loading && !analytics && !users.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="font-body text-xl text-muted-charcoal">Besig om te laai...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment py-24 px-6">
      <div className="container mx-auto max-w-7xl">
        <h1 className="font-heading text-4xl font-normal text-charcoal mb-8">
          Admin Paneel
        </h1>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-charcoal/10 overflow-x-auto">
          {['orders', 'analytics', 'users', 'content', 'invitations'].map((tab) => (
            <button
              key={tab}
              data-testid={`tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`font-ui text-sm uppercase tracking-widest pb-4 transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'text-gold border-b-2 border-gold'
                  : 'text-muted-charcoal hover:text-charcoal'
              }`}
            >
              {tab === 'orders' && 'Bestellings'}
              {tab === 'analytics' && 'Statistieke'}
              {tab === 'users' && 'Gebruikers'}
              {tab === 'content' && 'Inhoud'}
              {tab === 'invitations' && 'Uitnodigings'}
            </button>
          ))}
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="bg-gold/10 border border-gold/30 p-4 rounded-sm flex-1 mr-4">
                <p className="font-body text-charcoal">
                  Bestuur bestellings: opdateer afleweringstatus en voeg spoorsnommers by.
                </p>
              </div>
              <button
                onClick={exportOrders}
                disabled={orders.length === 0}
                className="bg-green-600 text-white hover:bg-green-700 rounded px-4 py-2 font-ui uppercase tracking-widest text-xs disabled:opacity-50 whitespace-nowrap"
              >
                Export na Excel
              </button>
            </div>
            
            {orders.length === 0 ? (
              <div className="bg-white border border-[#E5E0D6] p-8 text-center">
                <p className="font-body text-muted-charcoal">Geen bestellings nog nie.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.purchase_id} className="bg-white border border-[#E5E0D6] p-6">
                    <div className="flex flex-wrap justify-between items-start mb-4 gap-2">
                      <div className="flex flex-wrap gap-2">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-ui uppercase ${
                          order.payment_status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {order.payment_status === 'completed' ? 'Betaal' : 'Hangend'}
                        </span>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-ui uppercase ${
                          order.purchase_type === 'digital' ? 'bg-purple-100 text-purple-800' :
                          order.purchase_type === 'physical' ? 'bg-blue-100 text-blue-800' :
                          'bg-indigo-100 text-indigo-800'
                        }`}>
                          {order.purchase_type === 'physical' ? 'Fisies' : 
                           order.purchase_type === 'both' ? 'Digitaal + Fisies' : 'Digitaal'}
                        </span>
                        {(order.purchase_type === 'physical' || order.purchase_type === 'both') && (
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-ui uppercase ${getStatusColor(order.shipping_status || 'pending')}`}>
                            {getStatusLabel(order.shipping_status || 'pending')}
                          </span>
                        )}
                      </div>
                      <p className="font-heading text-xl text-gold">R{order.amount?.toFixed(2) || '0.00'}</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-ui text-xs uppercase tracking-widest text-muted-charcoal mb-2">
                          Klient Besonderhede
                        </h3>
                        <p className="font-body text-charcoal font-medium">{order.user_name}</p>
                        <p className="font-body text-charcoal">{order.user_email}</p>
                        {order.phone_number && (
                          <p className="font-body text-charcoal">Tel: {order.phone_number}</p>
                        )}
                      </div>
                      
                      {order.shipping_address && (
                        <div>
                          <h3 className="font-ui text-xs uppercase tracking-widest text-muted-charcoal mb-2">
                            Afleweringsadres
                          </h3>
                          <p className="font-body text-charcoal whitespace-pre-line">{order.shipping_address}</p>
                        </div>
                      )}
                    </div>

                    {order.tracking_number && (
                      <div className="mt-4 p-3 bg-surface/30 rounded">
                        <p className="font-ui text-xs uppercase tracking-widest text-muted-charcoal">Spoorsnommer</p>
                        <p className="font-body text-charcoal font-medium">{order.tracking_number}</p>
                      </div>
                    )}
                    
                    {/* Status Update Section */}
                    {(order.purchase_type === 'physical' || order.purchase_type === 'both') && order.payment_status === 'completed' && (
                      <div className="mt-4 pt-4 border-t border-[#E5E0D6]">
                        {editingOrder === order.purchase_id ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="font-ui text-xs uppercase tracking-widest text-muted-charcoal block mb-2">
                                  Afleweringstatus
                                </label>
                                <select
                                  value={orderUpdate.shipping_status}
                                  onChange={(e) => setOrderUpdate({...orderUpdate, shipping_status: e.target.value})}
                                  className="w-full bg-transparent border border-charcoal/20 rounded px-3 py-2 font-body outline-none focus:border-gold"
                                >
                                  <option value="">Kies status...</option>
                                  <option value="pending">Hangend</option>
                                  <option value="processing">Verwerk</option>
                                  <option value="shipped">Gestuur</option>
                                  <option value="delivered">Afgelewer</option>
                                </select>
                              </div>
                              <div>
                                <label className="font-ui text-xs uppercase tracking-widest text-muted-charcoal block mb-2">
                                  Spoorsnommer (opsioneel)
                                </label>
                                <input
                                  type="text"
                                  value={orderUpdate.tracking_number}
                                  onChange={(e) => setOrderUpdate({...orderUpdate, tracking_number: e.target.value})}
                                  placeholder="bv. TRACK123456"
                                  className="w-full bg-transparent border border-charcoal/20 rounded px-3 py-2 font-body outline-none focus:border-gold"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => updateOrderStatus(order.purchase_id)}
                                className="bg-gold text-white hover:bg-gold/80 rounded px-4 py-2 font-ui uppercase tracking-widest text-xs"
                              >
                                Stoor
                              </button>
                              <button
                                onClick={() => {
                                  setEditingOrder(null);
                                  setOrderUpdate({ shipping_status: '', tracking_number: '' });
                                }}
                                className="bg-gray-200 text-charcoal hover:bg-gray-300 rounded px-4 py-2 font-ui uppercase tracking-widest text-xs"
                              >
                                Kanselleer
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingOrder(order.purchase_id);
                              setOrderUpdate({
                                shipping_status: order.shipping_status || 'pending',
                                tracking_number: order.tracking_number || ''
                              });
                            }}
                            className="text-gold hover:text-gold/80 font-ui text-xs uppercase tracking-widest"
                          >
                            Opdateer Status
                          </button>
                        )}
                      </div>
                    )}
                    
                    <div className="mt-4 pt-4 border-t border-[#E5E0D6]">
                      <p className="font-ui text-xs text-muted-charcoal">
                        Bestelling: {order.purchase_id?.slice(0, 8)}... | Datum: {new Date(order.created_at).toLocaleString('af-ZA')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            {/* Reset Button */}
            <div className="flex justify-end">
              <button
                onClick={resetStatistics}
                className="bg-red-600 text-white hover:bg-red-700 rounded px-4 py-2 font-ui uppercase tracking-widest text-xs"
              >
                Reset Statistieke
              </button>
            </div>

            {/* Visitors Chart */}
            <div>
              <h2 className="font-heading text-2xl font-medium italic mb-4 text-charcoal">
                Besoeke (Laaste 14 Dae)
              </h2>
              <div className="bg-white border border-[#E5E0D6] p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics.daily_visits_chart || []}>
                    <defs>
                      <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#B8860B" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#B8860B" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D6" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={formatChartDate}
                      stroke="#666"
                      fontSize={12}
                    />
                    <YAxis stroke="#666" fontSize={12} />
                    <Tooltip 
                      labelFormatter={(value) => `Datum: ${value}`}
                      formatter={(value) => [`${value} besoeke`, 'Besoeke']}
                      contentStyle={{ 
                        backgroundColor: '#FAF8F5', 
                        border: '1px solid #E5E0D6',
                        borderRadius: '4px'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="visits" 
                      stroke="#B8860B" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorVisits)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Visitors Stats */}
            <div>
              <h2 className="font-heading text-2xl font-medium italic mb-4 text-charcoal">
                Besoeker Statistieke
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white border border-[#E5E0D6] p-6">
                  <p className="font-ui text-xs uppercase tracking-widest text-muted-charcoal mb-2">
                    Vandag se Besoeke
                  </p>
                  <p className="font-heading text-4xl text-gold">{analytics.today_visits || 0}</p>
                </div>

                <div className="bg-white border border-[#E5E0D6] p-6">
                  <p className="font-ui text-xs uppercase tracking-widest text-muted-charcoal mb-2">
                    Laaste 7 Dae
                  </p>
                  <p className="font-heading text-4xl text-charcoal">{analytics.weekly_visits || 0}</p>
                </div>

                <div className="bg-white border border-[#E5E0D6] p-6">
                  <p className="font-ui text-xs uppercase tracking-widest text-muted-charcoal mb-2">
                    Totale Bladsybesoeke
                  </p>
                  <p className="font-heading text-4xl text-charcoal">{analytics.total_visits || 0}</p>
                </div>

                <div className="bg-white border border-[#E5E0D6] p-6">
                  <p className="font-ui text-xs uppercase tracking-widest text-muted-charcoal mb-2">
                    Unieke Besoekers
                  </p>
                  <p className="font-heading text-4xl text-charcoal">{analytics.unique_visitors || 0}</p>
                </div>
              </div>
            </div>

            {/* Users & Sales Section */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading text-2xl font-medium italic text-charcoal">
                  Gebruikers & Verkope
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={resetSales}
                    className="bg-orange-600 text-white hover:bg-orange-700 rounded px-3 py-1 font-ui uppercase tracking-widest text-xs"
                  >
                    Reset Verkope
                  </button>
                  <button
                    onClick={resetUsers}
                    className="bg-red-800 text-white hover:bg-red-900 rounded px-3 py-1 font-ui uppercase tracking-widest text-xs"
                  >
                    Reset Gebruikers
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white border border-[#E5E0D6] p-6">
                  <p className="font-ui text-xs uppercase tracking-widest text-muted-charcoal mb-2">
                    Geregistreerde Gebruikers
                  </p>
                  <p className="font-heading text-4xl text-charcoal">{analytics.total_users}</p>
                  <p className="font-body text-sm text-muted-charcoal mt-2">
                    +{analytics.recent_users_7days} laaste 7 dae
                  </p>
                </div>

                <div className="bg-white border border-[#E5E0D6] p-6">
                  <p className="font-ui text-xs uppercase tracking-widest text-muted-charcoal mb-2">
                    Totale Aankope
                  </p>
                  <p className="font-heading text-4xl text-charcoal">{analytics.total_purchases}</p>
                  <p className="font-body text-sm text-muted-charcoal mt-2">
                    +{analytics.recent_purchases_7days} laaste 7 dae
                  </p>
                </div>

                <div className="bg-white border border-[#E5E0D6] p-6">
                  <p className="font-ui text-xs uppercase tracking-widest text-muted-charcoal mb-2">
                    Totale Inkomste
                  </p>
                  <p className="font-heading text-4xl text-gold">R{analytics.total_revenue?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button
                onClick={exportUsers}
                disabled={users.length === 0}
                className="bg-green-600 text-white hover:bg-green-700 rounded px-4 py-2 font-ui uppercase tracking-widest text-xs disabled:opacity-50"
              >
                Export na Excel
              </button>
            </div>
            <div className="bg-white border border-[#E5E0D6]">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface/30">
                  <tr>
                    <th className="px-6 py-4 text-left font-ui text-xs uppercase tracking-widest text-charcoal">
                      Naam
                    </th>
                    <th className="px-6 py-4 text-left font-ui text-xs uppercase tracking-widest text-charcoal">
                      E-pos
                    </th>
                    <th className="px-6 py-4 text-left font-ui text-xs uppercase tracking-widest text-charcoal">
                      Geregistreer
                    </th>
                    <th className="px-6 py-4 text-left font-ui text-xs uppercase tracking-widest text-charcoal">
                      Toegang
                    </th>
                    <th className="px-6 py-4 text-left font-ui text-xs uppercase tracking-widest text-charcoal">
                      Aksies
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.user_id} className="border-t border-[#E5E0D6]">
                      <td className="px-6 py-4 font-body text-charcoal">{user.name}</td>
                      <td className="px-6 py-4 font-body text-charcoal">{user.email}</td>
                      <td className="px-6 py-4 font-body text-sm text-muted-charcoal">
                        {new Date(user.created_at).toLocaleDateString('af-ZA')}
                      </td>
                      <td className="px-6 py-4">
                        {user.has_purchased || user.has_digital_access ? (
                          <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-ui uppercase">
                            {user.purchase_type || 'Digitaal'}
                          </span>
                        ) : (
                          <span className="inline-block bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-ui uppercase">
                            Geen
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {user.has_purchased || user.has_digital_access ? (
                          <button
                            onClick={() => revokeAccess(user.email)}
                            className="text-red-600 hover:text-red-800 font-ui text-xs uppercase tracking-widest"
                          >
                            Verwyder Toegang
                          </button>
                        ) : (
                          <button
                            onClick={() => grantAccess(user.email)}
                            className="text-gold hover:text-gold-dark font-ui text-xs uppercase tracking-widest"
                          >
                            Gee Toegang
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        )}

        {/* Content Tab (CMS) */}
        {activeTab === 'content' && (
          <div className="space-y-6">
            <div className="bg-gold/10 border border-gold/30 p-4 rounded-sm">
              <p className="font-body text-charcoal">
                Wysig teks op die webwerf se bladsye. Kies 'n bladsy en afdeling, en voer die nuwe inhoud in.
              </p>
            </div>

            {/* Add/Edit Content */}
            <div className="bg-white border border-[#E5E0D6] p-6">
              <h2 className="font-heading text-2xl font-medium italic mb-4 text-charcoal">
                {editingContent ? 'Wysig Inhoud' : 'Voeg Nuwe Inhoud By'}
              </h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-ui text-xs uppercase tracking-widest text-muted-charcoal block mb-2">
                      Bladsy
                    </label>
                    <select
                      value={contentUpdate.page}
                      onChange={(e) => setContentUpdate({...contentUpdate, page: e.target.value})}
                      className="w-full bg-transparent border border-charcoal/20 rounded px-3 py-2 font-body outline-none focus:border-gold"
                    >
                      <option value="">Kies bladsy...</option>
                      <option value="home">Tuisblad</option>
                      <option value="about">Oor die Boek</option>
                      <option value="author">Outeur</option>
                      <option value="excerpts">Uittreksels</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-ui text-xs uppercase tracking-widest text-muted-charcoal block mb-2">
                      Afdeling
                    </label>
                    <input
                      type="text"
                      value={contentUpdate.section}
                      onChange={(e) => setContentUpdate({...contentUpdate, section: e.target.value})}
                      placeholder="bv. title, subtitle, intro, description"
                      className="w-full bg-transparent border border-charcoal/20 rounded px-3 py-2 font-body outline-none focus:border-gold"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-ui text-xs uppercase tracking-widest text-muted-charcoal block mb-2">
                    Inhoud
                  </label>
                  <textarea
                    value={contentUpdate.content}
                    onChange={(e) => setContentUpdate({...contentUpdate, content: e.target.value})}
                    placeholder="Voer die nuwe teks in..."
                    rows={5}
                    className="w-full bg-transparent border border-charcoal/20 rounded px-3 py-2 font-body outline-none focus:border-gold resize-y"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={updateContent}
                    className="bg-charcoal text-parchment hover:bg-charcoal/80 rounded px-6 py-2 font-ui uppercase tracking-widest text-sm"
                  >
                    Stoor Inhoud
                  </button>
                  {editingContent && (
                    <button
                      onClick={() => {
                        setEditingContent(null);
                        setContentUpdate({ page: '', section: '', content: '' });
                      }}
                      className="bg-gray-200 text-charcoal hover:bg-gray-300 rounded px-6 py-2 font-ui uppercase tracking-widest text-sm"
                    >
                      Kanselleer
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Content List */}
            {content.length > 0 && (
              <div className="bg-white border border-[#E5E0D6]">
                <div className="p-4 border-b border-[#E5E0D6]">
                  <h3 className="font-heading text-xl text-charcoal">Bestaande Inhoud</h3>
                </div>
                <div className="divide-y divide-[#E5E0D6]">
                  {content.map((item) => (
                    <div key={item.page} className="p-4">
                      <h4 className="font-ui text-sm uppercase tracking-widest text-gold mb-2">
                        {item.page === 'home' ? 'Tuisblad' :
                         item.page === 'about' ? 'Oor die Boek' :
                         item.page === 'author' ? 'Outeur' :
                         item.page === 'excerpts' ? 'Uittreksels' : item.page}
                      </h4>
                      {item.sections && Object.entries(item.sections).map(([section, text]) => (
                        <div key={section} className="ml-4 mb-2 flex justify-between items-start">
                          <div>
                            <span className="font-ui text-xs text-muted-charcoal">{section}: </span>
                            <span className="font-body text-charcoal">{text?.substring(0, 100)}{text?.length > 100 ? '...' : ''}</span>
                          </div>
                          <button
                            onClick={() => {
                              setEditingContent(`${item.page}-${section}`);
                              setContentUpdate({ page: item.page, section, content: text });
                            }}
                            className="text-gold hover:text-gold/80 font-ui text-xs uppercase ml-2"
                          >
                            Wysig
                          </button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Invitations Tab */}
        {activeTab === 'invitations' && (
          <div className="space-y-6">
            {/* Create Invitation */}
            <div className="bg-white border border-[#E5E0D6] p-6">
              <h2 className="font-heading text-2xl font-medium italic mb-4 text-charcoal">
                Skep Nuwe Uitnodiging
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="email"
                  value={newInvitation.email}
                  onChange={(e) => setNewInvitation({ ...newInvitation, email: e.target.value })}
                  placeholder="E-pos adres"
                  className="bg-transparent border-b border-charcoal/20 focus:border-gold px-0 py-3 text-lg font-body outline-none"
                />
                <select
                  value={newInvitation.access_type}
                  onChange={(e) => setNewInvitation({ ...newInvitation, access_type: e.target.value })}
                  className="bg-transparent border-b border-charcoal/20 focus:border-gold px-0 py-3 text-lg font-body outline-none"
                >
                  <option value="digital">Digitaal</option>
                  <option value="physical">Fisies</option>
                  <option value="both">Beide</option>
                </select>
                <button
                  onClick={createInvitation}
                  className="bg-charcoal text-parchment hover:bg-charcoal/80 rounded-sm px-6 py-3 font-ui uppercase tracking-widest text-sm"
                >
                  Skep Uitnodiging
                </button>
              </div>
            </div>

            {/* Invitations List */}
            <div className="bg-white border border-[#E5E0D6]">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-surface/30">
                    <tr>
                      <th className="px-6 py-4 text-left font-ui text-xs uppercase tracking-widest text-charcoal">
                        E-pos
                      </th>
                      <th className="px-6 py-4 text-left font-ui text-xs uppercase tracking-widest text-charcoal">
                        Toegang Tipe
                      </th>
                      <th className="px-6 py-4 text-left font-ui text-xs uppercase tracking-widest text-charcoal">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left font-ui text-xs uppercase tracking-widest text-charcoal">
                        Geskep
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map((inv) => (
                      <tr key={inv.invitation_id} className="border-t border-[#E5E0D6]">
                        <td className="px-6 py-4 font-body text-charcoal">{inv.email}</td>
                        <td className="px-6 py-4 font-body text-sm text-muted-charcoal">
                          {inv.access_type}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-ui uppercase ${
                            inv.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            inv.status === 'accepted' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {inv.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-body text-sm text-muted-charcoal">
                          {new Date(inv.created_at).toLocaleDateString('af-ZA')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
